/**
 * CategoryGrid — top-level "Shop by category" tile grid on the home page.
 *
 * 4 columns on mobile, 6 on tablet, 8 on desktop. Tap routes the user into
 * the global Catalog filtered to that category.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SectionHeader, css } from '../../design';
import { Category } from '../../api/categories';

export interface CategoryGridProps {
  categories: Category[];
  /** Force the home variant (no section header / footer). */
  bare?: boolean;
}

const CategoryGrid: React.FC<CategoryGridProps> = ({ categories, bare }) => {
  const navigate = useNavigate();
  if (categories.length === 0) return null;

  return (
    <section style={{ marginBottom: 24 }}>
      {!bare && <SectionHeader title="🛍️ Shop by category" accent="primary" />}
      <div
        className="pm-category-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 10,
        }}
      >
        {categories.map((cat) => (
          <button
            key={cat.id}
            type="button"
            onClick={() => navigate(`/catalog?vertical=grocery&category=${encodeURIComponent(cat.name)}`)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
              padding: '14px 8px',
              background: css.color.surface,
              border: `1px solid ${css.color.border}`,
              borderRadius: 14,
              cursor: 'pointer',
              minHeight: 92,
              boxShadow: css.shadow.sm,
              transition: 'transform 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.borderColor = css.color.primary;
              e.currentTarget.style.boxShadow = css.shadow.cardHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = css.color.border;
              e.currentTarget.style.boxShadow = css.shadow.sm;
            }}
          >
            <span style={{ fontSize: 32, lineHeight: 1 }} aria-hidden>{cat.icon}</span>
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: css.color.text,
                textAlign: 'center',
                lineHeight: 1.3,
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {cat.name}
            </span>
          </button>
        ))}
      </div>

      <style>{`
        @media (min-width: 576px) { .pm-category-grid { grid-template-columns: repeat(6, 1fr) !important; } }
        @media (min-width: 992px) { .pm-category-grid { grid-template-columns: repeat(8, 1fr) !important; } }
      `}</style>
    </section>
  );
};

export default CategoryGrid;

